import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { User } from '../model/user';
import { getStoredUser, setStoredUser } from '../helpers/user-storage.util';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subscription, timer } from 'rxjs';
import { environment } from '../../environments/environment';
import { MiscService } from '../services/misc.service';
import { AuthenticationService } from '../services/authentication.service';
import { ToastrService } from 'ngx-toastr';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UserService } from '../services/user.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { DocumentService } from '../services/document.service';
import { PdfComponent } from '../pdf-viewer/pdf-viewer.component';
import { slugify } from '../shared/testid/testid.util';
import { WelcomeDialogComponent } from '../welcome-dialog/welcome-dialog.component';

// Shared by every Manage Account instance: a save sent from one that was since closed still blocks Verify in the next,
// so that save's late answer cannot overwrite a newer verification.
const profileSaves = { pending: 0, generation: 0 };

import { openVersionConflictDialog } from '../helpers/version-conflict.util';

/**
 * Manage Account — now a lean, single-section **Profile** page (CONNECT 2.0,
 * Spec 6 Â· Task 3). Services & Zones and Documents were lifted out to their own
 * routed pages (Tasks 1 & 2); the old Organization tab is hidden for now
 * (Product) with its data + save handlers left in place for when it returns
 * (Task 5). There is no tab bar any more â€” just the Profile card sections
 * (Task 4), plus a new "Company" card carrying the org fields Product wants
 * surfaced today (name/phone/addresses).
 */
@Component({
  selector: 'app-manage-account',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatDialogModule,
    ReactiveFormsModule,
    WelcomeDialogComponent,
  ],
  templateUrl: './manage-account.component.html',
  styleUrl: './manage-account.component.scss',
})
export class ManageAccountComponent implements OnDestroy {
  private subscriptions = new Subscription();
  private verificationPending = false;
  private verificationCooldown = false;

  get addressVerificationEnabled(): boolean {
    return environment.addressVerificationEnabled;
  }

  get addressVerificationNeeded(): boolean {
    return getStoredUser()?.addressVerificationNeeded === true;
  }

  get verifyAddressDisabled(): boolean {
    return this.verificationPending || this.verificationCooldown || profileSaves.pending > 0;
  }

  // Reads the flag fresh from storage (not this.user) so it reflects the latest
  // persisted value â€” it flips to unlocked the moment a completing save writes
  // welcomeEmailPending:false back to localStorage, with no re-login.
  get isLocked(): boolean {
    return getStoredUser()?.welcomeEmailPending === true;
  }
  emailRegx: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  id: string = '';
  user: User = {};
  // profile data
  protected nameUpdate = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
  });

  protected emailUpdate = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(this.emailRegx),
    ]),
  });
  phone: string = '';
  phone2: string = '';
  phoneOs: string = '';
  phone2Os: string = '';
  streetAddress: string = '';
  city: string = '';
  state: string = '';
  zipcode: string = '';
  ccName1: string = '';
  ccName2: string = '';
  ccEmail1: string = '';
  ccEmail2: string = '';
  ccPhone1: string = '';
  ccPhone2: string = '';
  bio: string = '';
  timezone: any = {};

  // Company (organization) data - name/phone/physical + mailing address are
  // surfaced on Profile's Company card (Task 4); email/website/description stay
  // captured but hidden (Task 5, Organization deferred).
  orgName: string = '';
  orgEmail: string = '';
  orgPhone: string = '';
  orgStreetAddress: string = '';
  orgCity: string = '';
  orgState: string = '';
  orgZipcode: string = '';
  orgWebsite: string = '';
  orgDescription: string = '';

  // Mailing address - persists to organization.mailingLocation (a second
  // Location on connect-service's Organization, alongside the physical
  // `location`). "Same as physical" is the default: when checked we persist a
  // null mailingLocation so the mailing address follows the physical one, and
  // the read view mirrors the physical values. Unchecking captures a distinct
  // mailing address that round-trips like any other org field.
  orgMailingSameAsPhysical: boolean = true;
  orgMailingStreetAddress: string = '';
  orgMailingCity: string = '';
  orgMailingState: string = '';
  orgMailingZipcode: string = '';

  // State/county catalog + time zones â€” used by the profile + company address
  // selects. (The Services & Zones capability/coverage state was lifted to the
  // standalone /services-zones page.)
  stateCountyDetails: any = [];
  timeZones: any = [];

  editNameToggle: boolean = false;
  editEmailToggle: boolean = false;
  editPhoneToggle: boolean = false;
  editAddressToggle: boolean = false;
  editCcInfoToggle: boolean = false;
  editProfileAdditionalInfoToggle: boolean = false;
  editOrgInfoToggle: boolean = false;
  editOrgAddressToggle: boolean = false;
  // Organization's tab is hidden (Task 5) so this toggle + saveOrgAdditionalInfo
  // are dead from the UI, but left in place for when Organization returns.
  editOrgAdditionalInfoToggle: boolean = false;
  updateSucess: boolean = false;

  openWelcomeDialog: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private miscService: MiscService,
    private toastr: ToastrService,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private utilityService: CommonUtilityServiceService,
    private documentService: DocumentService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    // Services & Zones and Documents were lifted to their own /services-zones
    // and /documents pages (Spec 6, Tasks 1 & 2). A stale `?tab=servicezones` /
    // `?tab=documents` deep link (e.g. from the setup-lock redirect) now
    // navigates straight there instead of rendering here. `?tab=organization`
    // (hidden, Task 5) and anything else fall through to the single Profile
    // view below.
    this.route.queryParams.subscribe((params) => {
      const requested = params['tab'];
      if (requested === 'documents') {
        this.router.navigate(['/documents']);
      } else if (requested === 'servicezones') {
        this.router.navigate(['/services-zones']);
      }
    });
    this.user = getStoredUser();
    if (this.user?.newUser) {
      this.markUserOnboarded();
    }
    // this.openWelcomeDialog = this.user?.newUser || false;
    // turning off the welcome dialog, keeping it just in case we turn it back on
    this.openWelcomeDialog = false;
    this.miscService.fetchStateCountyDetails().subscribe((result: any) => {
      this.stateCountyDetails = result;
    });

    this.miscService.fetchTimeZones().subscribe((result: any) => {
      this.timeZones = result;
    });

    if (this.user) {
      this.populateFieldsFromUser();
    }
  }

  // Populates every local field from `this.user` — the ngOnInit prefill, and
  // also re-run after a reload following a version-conflict dialog (see
  // `reloadUserAfterConflict`) so the form reflects the current authoritative
  // record before the vendor re-applies their edit.
  private populateFieldsFromUser(): void {
    this.nameUpdate.patchValue({
      firstName: this.user.firstName,
      lastName: this.user.lastName,
    });
    this.emailUpdate.patchValue({
      email: this.user.email,
    });
    this.phone = this.user.phone ?? '';
    this.phoneOs = this.user.phoneOs ?? '';
    this.phone2 = this.user.phone2 ?? '';
    this.phone2Os = this.user.phone2Os ?? '';
    // populating the user's address
    const location = this.user.location;
    if (location) {
      this.streetAddress = location.street ?? '';
      this.city = location.city ?? '';
      if (location.state) {
        this.state = location.state;
      }
      this.zipcode = location.zipcode ?? '';
    }
    this.ccName1 = this.user.ccName1 ?? '';
    this.ccName2 = this.user.ccName2 ?? '';
    this.ccEmail1 = this.user.ccEmail1 ?? '';
    this.ccEmail2 = this.user.ccEmail2 ?? '';
    this.ccPhone1 = this.user.ccPhone1 ?? '';
    this.ccPhone2 = this.user.ccPhone2 ?? '';
    this.bio = this.user.bio ?? '';
    if (this.user.timezone) {
      this.timezone = this.user.timezone;
    }
    const organization = this.user.organization;
    if (organization) {
      this.orgName = organization.name;
      this.orgEmail = organization.email;
      this.orgPhone = organization.phone;
      const orgLocation = organization.location;
      if (orgLocation) {
        this.orgStreetAddress = orgLocation.street;
        this.orgCity = orgLocation.city;
        this.orgState = orgLocation.state;
        this.orgZipcode = orgLocation.zipcode;
      }
      // A stored mailingLocation means the vendor chose a distinct mailing
      // address; uncheck "same as physical" and populate the fields. A null/
      // empty mailingLocation leaves the default (checked → mirrors physical).
      const orgMailing = organization.mailingLocation;
      if (
        orgMailing &&
        (orgMailing.street ||
          orgMailing.city ||
          orgMailing.state ||
          orgMailing.zipcode)
      ) {
        this.orgMailingSameAsPhysical = false;
        this.orgMailingStreetAddress = orgMailing.street ?? '';
        this.orgMailingCity = orgMailing.city ?? '';
        this.orgMailingState = orgMailing.state ?? '';
        this.orgMailingZipcode = orgMailing.zipcode ?? '';
      }
      this.orgWebsite = organization.website;
      this.orgDescription = organization.description;
    }
  }

  decodeState(state: string): string {
    return this.utilityService.decodeUsState(state);
  }

  // Initials for the avatar â€” the photo upload is retired (Product), so the
  // header shows initials, falling back to a generic person icon when neither
  // name is available yet.
  get userInitials(): string {
    const first = (this.user.firstName ?? '').trim().charAt(0);
    const last = (this.user.lastName ?? '').trim().charAt(0);
    return `${first}${last}`.toUpperCase();
  }

  toggleWelcomeDialog() {
    this.openWelcomeDialog = !this.openWelcomeDialog;
    this.markUserOnboarded();
  }

  // Resets the new-user onboarding flag once: clears newUser and stamps the
  // first login date. Shared by the first-login redirect (ngOnInit) and the
  // welcome dialog so the reset logic lives in one place.
  private markUserOnboarded() {
    this.updateUser(
      {
        ...this.user,
        newUser: false,
        firstLogInDate: new Date(),
      },
      false,
    );
  }

  editName() {
    this.editNameToggle = !this.editNameToggle;
  }

  editEmail() {
    this.editEmailToggle = !this.editEmailToggle;
  }

  editPhone() {
    this.editPhoneToggle = !this.editPhoneToggle;
  }

  editCCInfo() {
    this.editCcInfoToggle = !this.editCcInfoToggle;
  }

  editAddress() {
    this.editAddressToggle = !this.editAddressToggle;
  }

  editProfileAddtionalInfo() {
    this.editProfileAdditionalInfoToggle =
      !this.editProfileAdditionalInfoToggle;
  }

  editOrgInfo() {
    this.editOrgInfoToggle = !this.editOrgInfoToggle;
  }

  editOrgAddress() {
    this.editOrgAddressToggle = !this.editOrgAddressToggle;
  }

  editOrgAdditionalInfo() {
    this.editOrgAdditionalInfoToggle = !this.editOrgAdditionalInfoToggle;
  }

  // Treats null/undefined and '' as equivalent so an unset field (undefined on
  // the user) doesn't read as a change against an empty input ('') and fire a
  // spurious save/PUT. Used by the change-detection guards below.
  private differs(a: any, b: any): boolean {
    return (a ?? '') !== (b ?? '');
  }

  saveName() {
    if (!this.nameUpdate.valid) {
      this.toastr.error('First name and last name are required.');
    } else if (
      this.differs(this.nameUpdate.value.firstName, this.user.firstName) ||
      this.differs(this.nameUpdate.value.lastName, this.user.lastName)
    ) {
      this.updateUser(
        {
          ...this.user,
          firstName: this.nameUpdate.value.firstName ?? '',
          lastName: this.nameUpdate.value.lastName ?? '',
        },
        true,
      );
      this.editNameToggle = !this.editNameToggle;
    } else {
      this.editNameToggle = !this.editNameToggle;
    }
  }

  savePhone() {
    if (
      this.differs(this.phone, this.user.phone) ||
      this.differs(this.phoneOs, this.user.phoneOs) ||
      this.differs(this.phone2, this.user.phone2) ||
      this.differs(this.phone2Os, this.user.phone2Os)
    ) {
      this.updateUser(
        {
          ...this.user,
          phone: this.phone,
          phoneOs: this.phoneOs,
          phone2: this.phone2,
          phone2Os: this.phone2Os,
        },
        true,
      );
    }
    this.editPhoneToggle = !this.editPhoneToggle;
  }

  saveEmail() {
    if (!this.emailUpdate.valid) {
      this.toastr.error('Please enter a valid email.');
    } else if (
      this.emailUpdate.value.email &&
      this.emailUpdate.value.email !== this.user.email
    ) {
      this.userService
        .updateUserEmail({
          ...this.user,
          email: this.emailUpdate.value.email.toLowerCase(),
        })
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.code === 409) {
              this.showVersionConflictDialog();
            } else if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              setStoredUser(this.user);
              this.toastr.success(apiResponse.message);
              this.editEmailToggle = !this.editEmailToggle;
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    } else {
      this.editEmailToggle = !this.editEmailToggle;
    }
  }

  saveAddress() {
    const location = this.user.location;
    if (
      this.differs(this.streetAddress, location?.street) ||
      this.differs(this.city, location?.city) ||
      (this.state && this.differs(this.state, location?.state)) ||
      this.differs(this.zipcode, location?.zipcode)
    ) {
      let updatedLocation = {
        ...location,
        street: this.streetAddress,
        city: this.city,
        zipcode: this.zipcode,
      };
      if (this.state) {
        updatedLocation = { ...updatedLocation, state: this.state };
      }
      this.updateUser({ ...this.user, location: updatedLocation }, true);
    }
    this.editAddressToggle = !this.editAddressToggle;
  }

  saveCCInfo() {
    if (
      this.differs(this.ccName1, this.user.ccName1) ||
      this.differs(this.ccName2, this.user.ccName2) ||
      this.differs(this.ccEmail1, this.user.ccEmail1) ||
      this.differs(this.ccEmail2, this.user.ccEmail2) ||
      this.differs(this.ccPhone1, this.user.ccPhone1) ||
      this.differs(this.ccPhone2, this.user.ccPhone2)
    ) {
      this.updateUser(
        {
          ...this.user,
          ccName1: this.ccName1,
          ccName2: this.ccName2,
          ccEmail1: this.ccEmail1,
          ccEmail2: this.ccEmail2,
          ccPhone1: this.ccPhone1,
          ccPhone2: this.ccPhone2,
        },
        true,
      );
    }
    this.editCcInfoToggle = !this.editCcInfoToggle;
  }

  saveProfileAdditionalInfo() {
    if (
      this.differs(this.bio, this.user.bio) ||
      this.differs(this.timezone.id, this.user.timezone?.id)
    ) {
      this.updateUser(
        { ...this.user, bio: this.bio, timezone: this.timezone },
        true,
      );
    }
    this.editProfileAdditionalInfoToggle =
      !this.editProfileAdditionalInfoToggle;
  }

  saveOrgInfo() {
    if (
      this.differs(this.orgName, this.user.organization?.name) ||
      this.differs(this.orgEmail, this.user.organization?.email) ||
      this.differs(this.orgPhone, this.user.organization?.phone)
    ) {
      const updatedOrg = {
        ...this.user.organization,
        name: this.orgName,
        email: this.orgEmail,
        phone: this.orgPhone,
      };
      this.updateUser(
        {
          ...this.user,
          organization: updatedOrg,
        },
        true,
      );
    }
    this.editOrgInfoToggle = !this.editOrgInfoToggle;
  }

  saveOrgAddress() {
    const orgLocation = this.user.organization?.location;
    if (
      this.differs(this.orgStreetAddress, orgLocation?.street) ||
      this.differs(this.orgCity, orgLocation?.city) ||
      (this.orgState && this.differs(this.orgState, orgLocation?.state)) ||
      this.differs(this.orgZipcode, orgLocation?.zipcode)
    ) {
      let updatedLocation = {
        ...orgLocation,
        street: this.orgStreetAddress,
        city: this.orgCity,
        zipcode: this.orgZipcode,
      };
      if (this.orgState) {
        updatedLocation = { ...updatedLocation, state: this.orgState };
      }
      const updatedOrg = {
        ...this.user.organization,
        location: updatedLocation,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, true);
    }
    this.editOrgAddressToggle = !this.editOrgAddressToggle;
  }

  saveOrgAdditionalInfo() {
    if (
      this.differs(this.orgDescription, this.user.organization?.description) ||
      this.differs(this.orgWebsite, this.user.organization?.website)
    ) {
      const updatedOrg = {
        ...this.user.organization,
        description: this.orgDescription,
        website: this.orgWebsite,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, false);
    }
    this.editOrgAdditionalInfoToggle = !this.editOrgAdditionalInfoToggle;
  }

  // Combined Save for the Company Address card. Persists the physical address
  // (organization.location) and the mailing address (organization.mailingLocation)
  // in a single update. When "same as physical" is checked the mailingLocation is
  // cleared to null so it always follows the physical address; otherwise the
  // entered mailing address is persisted. Existing physical/mailing objects are
  // spread through so server-set fields (e.g. geoPoint, county) are preserved.
  saveCompanyAddress() {
    const orgLocation = this.user.organization?.location;
    const existingMailing = this.user.organization?.mailingLocation;

    let updatedLocation = {
      ...orgLocation,
      street: this.orgStreetAddress,
      city: this.orgCity,
      zipcode: this.orgZipcode,
    };
    if (this.orgState) {
      updatedLocation = { ...updatedLocation, state: this.orgState };
    }

    let updatedMailingLocation: any = null;
    if (!this.orgMailingSameAsPhysical) {
      updatedMailingLocation = {
        ...existingMailing,
        street: this.orgMailingStreetAddress,
        city: this.orgMailingCity,
        zipcode: this.orgMailingZipcode,
      };
      if (this.orgMailingState) {
        updatedMailingLocation = {
          ...updatedMailingLocation,
          state: this.orgMailingState,
        };
      }
    }

    const physicalChanged =
      this.differs(this.orgStreetAddress, orgLocation?.street) ||
      this.differs(this.orgCity, orgLocation?.city) ||
      (this.orgState && this.differs(this.orgState, orgLocation?.state)) ||
      this.differs(this.orgZipcode, orgLocation?.zipcode);

    const mailingChanged = this.orgMailingSameAsPhysical
      ? !!existingMailing
      : this.differs(this.orgMailingStreetAddress, existingMailing?.street) ||
        this.differs(this.orgMailingCity, existingMailing?.city) ||
        (this.orgMailingState &&
          this.differs(this.orgMailingState, existingMailing?.state)) ||
        this.differs(this.orgMailingZipcode, existingMailing?.zipcode);

    if (physicalChanged || mailingChanged) {
      const updatedOrg = {
        ...this.user.organization,
        location: updatedLocation,
        mailingLocation: updatedMailingLocation,
      };
      this.updateUser({ ...this.user, organization: updatedOrg }, true);
    }
    this.editOrgAddressToggle = !this.editOrgAddressToggle;
  }

  onMailingSameAsPhysicalChange() {
    if (this.orgMailingSameAsPhysical) {
      // Mirrors physical now â€” clear any local-only draft so it doesn't
      // resurface if the vendor unchecks this again later in the session.
      this.orgMailingStreetAddress = '';
      this.orgMailingCity = '';
      this.orgMailingState = '';
      this.orgMailingZipcode = '';
    }
  }

  // ---------------------------------------------------------------------------
  // Profile documents (Resume / Certifications / Licenses). Moved here from the
  // standalone Documents page so they live alongside the rest of the vendor's
  // profile; Zone Documents stay on the Documents page. The cards bind to
  // user.documents, which is sourced from the cached user read in ngOnInit via
  // getStoredUser(); upload/delete splice the server's updated user back into
  // this.user + localStorage so the list refreshes in place (no separate
  // ReplaySubject-backed fetch exists for profile documents).
  // ---------------------------------------------------------------------------

  // Stable testid part for a profile document row, keyed on its id (falls back
  // to the filename only for a not-yet-persisted doc that has none).
  profileDocTestKey(doc: any): string {
    return slugify(String(doc?.id ?? doc?.fileName ?? 'document'));
  }

  uploadProfileDocument(event: any) {
    if (event.target.files) {
      this.toastr.info('Uploading profile documents...');
      this.documentService
        .uploadProfileDocuments(this.user, event.target.files)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.code === 409) {
              this.showVersionConflictDialog();
            } else if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              setStoredUser(this.user);
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  deleteProfileDocument(documentId: string) {
    if (this.user.id) {
      this.documentService
        .deleteProfileDocument(this.user.id, documentId)
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.code === 409) {
              this.showVersionConflictDialog();
            } else if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              setStoredUser(this.user);
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  downloadDocument(document: any) {
    this.toastr.info('Downloading document...');
    this.documentService.downloadDocument(document);
  }

  // Only PDFs can be previewed in the in-app viewer, so the template gates the
  // eye icon on this — non-PDF uploads (docx, images, etc.) are download-only.
  isPdf(document: any): boolean {
    return !!document?.fileName && /\.pdf$/i.test(document.fileName);
  }

  openPdfViewer(document: any): void {
    this.documentService
      .downloadPdfToViewer(document)
      .then((result) => {
        this.dialog.open(PdfComponent, {
          height: '90%',
          width: '50%',
          panelClass: 'pdf-viewer-dialog-container',
          data: { pdfSrc: result.url, filename: result.filename },
        });
      })
      .catch(() => {
        this.toastr.error('Preview unavailable due to an error.');
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  verifyAddress(): void {
    if (this.verifyAddressDisabled) {
      return;
    }
    const profileSaveGeneration = profileSaves.generation;
    this.verificationPending = true;
    this.subscriptions.add(this.userService.verifyAddress().subscribe({
      next: (response) => {
        this.verificationPending = false;
        if (profileSaveGeneration !== profileSaves.generation) {
          this.toastr.info('Your profile changed while we were checking your address. Try again.');
          return;
        }
        if (!response?.data) {
          this.toastr.error('An error occurred. Please try again.');
          return;
        }
        this.storeVerificationFlag(response.data.addressVerificationNeeded);
        if (response.status === 'SUCCESS') {
          this.toastr.success(response.message || 'Address verified.');
        } else {
          this.toastr.error(response.message || 'An error occurred. Please try again.');
          if (response.message !== 'Your address changed while we were checking it. Try again.') {
            this.verificationCooldown = true;
            this.subscriptions.add(timer(30000).subscribe(() => {
              this.verificationCooldown = false;
            }));
          }
        }
      },
      error: () => {
        this.verificationPending = false;
        this.toastr.error('An error occurred. Please try again.');
      },
    }));
  }

  private storeUpdatedUser(updatedUser: User): void {
    this.user = { ...this.user, ...updatedUser };
    setStoredUser(this.user);
  }

  // Merged into the latest stored user, not this page's copy: a save sent from a Manage Account closed since this one
  // opened may have stored a newer profile, which writing this page's copy back would undo.
  private storeVerificationFlag(addressVerificationNeeded: boolean | undefined): void {
    this.user = { ...this.user, ...getStoredUser(), addressVerificationNeeded };
    setStoredUser(this.user);
  }

  private updateUser(user: User, displayMessge: boolean) {
    profileSaves.generation++;
    profileSaves.pending++;
    this.userService.updateUser(user).pipe(
      finalize(() => profileSaves.pending--),
    ).subscribe((response: any) => {
      const apiResponse = response;
      if (apiResponse.status === 'FAILURE') {
        if (apiResponse.code === 409) {
          this.showVersionConflictDialog();
        } else if (apiResponse.message) {
          this.toastr.error(apiResponse.message);
        } else {
          this.toastr.error('An error occurred. Please try again.');
        }
      } else {
        const updatedUser = response.data;
        if (updatedUser) {
          this.storeUpdatedUser(updatedUser);
          if (displayMessge) {
            this.toastr.success(apiResponse.message);
          }
        } else {
          this.toastr.error('An error occurred. Please try again.');
        }
      }
    });
  }

  // A `409` FAILURE envelope means another writer changed this record since it
  // was loaded (the optimistic-lock guard rejected a stale `version`) —
  // retrying the same save would just conflict again, so show the
  // reload-and-reapply dialog instead of a generic error toast. The reload
  // itself runs once the vendor acknowledges the dialog (it's `disableClose`,
  // so acknowledging is the only way to close it).
  private showVersionConflictDialog(): void {
    openVersionConflictDialog(this.dialog).subscribe(() => this.reloadUserAfterConflict());
  }

  // Refetches the authoritative profile and re-populates every field from it,
  // so the vendor sees the current values — including the version that just
  // rejected their save — before re-applying their edit.
  private reloadUserAfterConflict(): void {
    this.authenticationService.me().subscribe({
      next: (user) => {
        this.user = user;
        this.populateFieldsFromUser();
      },
      error: () =>
        this.toastr.error(
          'Failed to reload your account. Please refresh the page.',
        ),
    });
  }
}
